/**
 * hooks/useTickerAnalysis.ts
 * Manages ticker analysis — fetches signals, chain, news, plan.
 * Gracefully handles missing signals (pre-market / no IEX data).
 */

import { useCallback } from 'react';
import { analysisApi } from '../lib/api';
import { useStore, useTicker } from '../store';
import type { Gapper } from '../types';

function fallbackMsg(g: Gapper): string {
  const s = g.score ?? 0;
  if (s >= 70) return 'Score ' + s + '/100 — Strong setup. Wait for the 15-min candle to close above the pre-market high on 2x volume before entering.';
  if (s >= 50) return 'Score ' + s + '/100 — Moderate setup. Reduce size and confirm MACD is positive before entry.';
  return 'Score ' + s + '/100 — Low probability. Multiple key factors missing — consider skipping.';
}

function noSignalMsg(sym: string, g: Gapper): string {
  const s = g.score ?? 0;
  const verdict =
    s >= 70 ? 'Score ' + s + '/100 — High probability gap. Confirm signals at 9:30 AM open.'
    : s >= 50 ? 'Score ' + s + '/100 — Moderate setup. Needs signal confirmation before entering.'
    : 'Score ' + s + '/100 — Low probability. Skip unless signals improve at open.';

  return sym + ' — Score ' + s + '/100\n\n' +
    'Technical signals are unavailable right now — market is pre-open or Alpaca has no bar data for this ticker yet.\n\n' +
    'What you can do now:\n' +
    '  - Check the News tab — verify the catalyst is a real event\n' +
    '  - Review the options chain for liquidity (spread ≤ $0.15, OI ≥ 500)\n' +
    '  - Re-click this ticker after 9:30 AM ET for full signal analysis\n\n' +
    verdict;
}

export function useTickerAnalysis() {
  const ticker = useTicker();

  const analyze = useCallback(async (sym: string, gapper: Gapper) => {
    ticker.selectTicker(sym, gapper);
    ticker.setLoading(true);
    ticker.clearAiMessages();

    // Guards against a slower, now-superseded request overwriting the
    // ticker the user has since switched to.
    const isStale = () => useStore.getState().sym !== sym;

    try {
      // Run signals, options, and news in parallel
      const [signals, chain, news] = await Promise.allSettled([
        analysisApi.signals(sym),
        analysisApi.options(sym, undefined, gapper.price),
        analysisApi.news(sym),
      ]);

      if (isStale()) return;

      if (signals.status === 'fulfilled') ticker.setSignals(signals.value);
      if (chain.status   === 'fulfilled') ticker.setChain(chain.value);
      if (news.status    === 'fulfilled') ticker.setNews(news.value);

      // Always attempt plan — works even without signals
      try {
        const plan = await analysisApi.plan(sym, gapper.price);
        if (!isStale()) ticker.setPlan(plan);
      } catch (err) {
        console.warn('[Analysis] Plan failed:', err);
      }

      if (isStale()) return;

      // AI coaching message
      ticker.setAiLoading(true);

      if (signals.status === 'fulfilled') {
        // Signals available — request full AI analysis
        const bestCall = chain.status === 'fulfilled'
          ? (chain.value.contracts?.find((c: { classification: string }) =>
              c.classification === 'otm-1') ?? null)
          : null;

        // Prefer the real per-factor breakdown from the scan (so the AI
        // coach sees which factors were actually evaluated); fall back to
        // a bare total-only shape if a gapper was somehow selected without one.
        const score = gapper.scoreBreakdown ?? {
          total:          gapper.score ?? 0,
          verdict:        (gapper.score ?? 0) >= 70 ? 'high-probability' as const
                        : (gapper.score ?? 0) >= 50 ? 'moderate'         as const
                        : 'low-probability' as const,
          recommendation: (gapper.score ?? 0) >= 70 ? 'enter-full' as const
                        : (gapper.score ?? 0) >= 50 ? 'enter-half' as const
                        : 'skip'       as const,
          factors: [],
          evaluatedFactors: 0,
          totalFactors: 13,
        };

        try {
          const result = await analysisApi.ai(sym, {
            gapper,
            signals: signals.value,
            score,
            bestCall,
          });
          if (!isStale()) {
            ticker.setAiAnalysis(result.analysis);
            ticker.addAiMessage('assistant', result.analysis);
          }
        } catch (err) {
          console.warn('[Analysis] AI failed:', err);
          if (!isStale()) ticker.addAiMessage('assistant', fallbackMsg(gapper));
        }
      } else {
        // No signals — give pre-market guidance
        if (!isStale()) ticker.addAiMessage('assistant', noSignalMsg(sym, gapper));
      }

      if (!isStale()) ticker.setAiLoading(false);

    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Analysis failed';
      if (!isStale()) ticker.setError(msg);
    } finally {
      if (!isStale()) ticker.setLoading(false);
    }
  }, [ticker]);

  const sendChatMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;
    const { sym, gapper, aiMessages } = ticker;
    ticker.addAiMessage('user', content);
    ticker.setAiLoading(true);
    const messages = [...aiMessages, { role: 'user' as const, content }];
    try {
      const result = await analysisApi.chat(messages, {
        sym:       sym ?? undefined,
        changePct: gapper?.changePct,
        score:     gapper?.score,
      });
      ticker.addAiMessage('assistant', result.reply);
    } catch {
      ticker.addAiMessage('assistant', 'Connection error — check the server is running.');
    } finally {
      ticker.setAiLoading(false);
    }
  }, [ticker]);

  return { analyze, sendChatMessage };
}
